import { setGlobalCSSVar } from '@/lib/utils'
import { useEffect, useLayoutEffect, useState } from 'react'
import { z } from 'zod'

type ChatWidgetSettings = {
  onSuggestedQuestion: (question: string) => void
}

const configDataSchema = z.object({
  primaryColor: z.string(),
  backgroundColor: z.string(),
  textColor: z.string(),
})

const configMessageSchema = z.object({
  messageType: z.literal('config'),
  data: configDataSchema,
})

const suggestedQuestionMessageSchema = z.object({
  messageType: z.literal('suggestedQuestion'),
  data: z.string(),
})

const messageSchema = z.discriminatedUnion('messageType', [configMessageSchema, suggestedQuestionMessageSchema])

export function useHandleIFrameMessages(settings: ChatWidgetSettings) {
  const [config, setConfig] = useState<{
    primaryColor: string
    backgroundColor: string
    textColor: string
  }>()

  useLayoutEffect(() => {
    if (!config) return

    setGlobalCSSVar('--sensay-widget-primary-color', config.primaryColor)
    setGlobalCSSVar('--sensay-widget-background-color', config.backgroundColor)
    setGlobalCSSVar('--sensay-widget-foreground-color', config.textColor)
  }, [config])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const payload = event.data

      const parsed = messageSchema.safeParse(payload)

      if (!parsed.success) return

      const { messageType, data } = parsed.data

      switch (messageType) {
        case 'config':
          setConfig((prevConfig) => ({
            ...prevConfig,
            primaryColor: data.primaryColor,
            backgroundColor: data.backgroundColor,
            textColor: data.textColor,
          }))
          return
        case 'suggestedQuestion':
          settings.onSuggestedQuestion(data)
          return
      }
    }

    window.addEventListener('message', handleMessage)

    return () => {
      window.removeEventListener('message', handleMessage)
    }
  }, [])
}
